import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";
import { LAST_UPDATED, legal, privacy } from "../lib/legalContent";

const DOCS = { legal, privacy };
const ICONS = { legal: ScrollText, privacy: ShieldCheck };

export default function LegalPage({ doc }) {
  const { t, lang } = useI18n();
  const content = DOCS[doc][lang] || DOCS[doc].en;
  const Icon = ICONS[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${content.title} — AIwatermark.studio`;
  }, [content.title]);

  return (
    <main className="min-h-screen bg-[#050505]" data-testid={`legal-page-${doc}`}>
      <div className="relative overflow-hidden border-b border-white/5 grain">
        <div className="absolute -top-32 left-1/3 h-72 w-[32rem] -translate-x-1/2 rounded-full bg-sky-500/10 blur-[120px]" />
        <div className="relative mx-auto max-w-3xl px-5 pt-16 pb-12">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-[13px] text-slate-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            {t("nav.back")}
          </Link>
          <Icon className="mt-8 h-6 w-6 text-sky-400" strokeWidth={1.5} />
          <h1 className="mt-5 font-head text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="legal-title">
            {content.title}
          </h1>
          <p className="mt-3 font-mono2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
            {t("legal.updated")} {LAST_UPDATED[lang] || LAST_UPDATED.en}
          </p>
          <p className="mt-6 text-base leading-relaxed text-slate-400">{content.intro}</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 py-16">
        <div className="space-y-12">
          {content.sections.map((s, i) => (
            <section key={s.h} data-testid={`legal-section-${i}`} className="border-t border-white/10 pt-6">
              <span className="font-mono2 text-xs text-sky-400">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-3 font-head text-lg font-medium text-white">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.p.map((para) => (
                  <p key={para} className="text-sm leading-relaxed text-slate-400">
                    {para}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <Link
          to="/studio"
          data-testid="legal-studio-cta"
          className="mt-16 inline-flex items-center rounded-full bg-white/10 px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-white/20 active:scale-[0.98]"
        >
          {t("nav.open")}
        </Link>
      </div>
    </main>
  );
}
